import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkle,
  ArrowLeft,
  Check,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const STEPS = [
  {
    key: "face_shape",
    title: "What is your face shape?",
    options: [
      { value: "Oval", desc: "Suits any style" },
      { value: "Round", desc: "Long danglers elongate" },
      { value: "Square", desc: "Hoops soften angles" },
      { value: "Heart", desc: "Wider bottom drops balance" },
      { value: "Long", desc: "Studs and chokers add width" },
    ],
  },
  {
    key: "skin_tone",
    title: "What is your skin tone?",
    options: [
      { value: "Very Fair", color: "#F8E5D2" },
      { value: "Fair", color: "#E8C9A4" },
      { value: "Wheatish", color: "#C99A6B" },
      { value: "Medium Brown", color: "#8B5A2B" },
      { value: "Deep", color: "#5C3317" },
    ],
  },
  {
    key: "occasion",
    title: "What is the occasion?",
    options: [
      { value: "Bridal Wedding" },
      { value: "Reception / Party" },
      { value: "Festival" },
      { value: "Office Daily" },
      { value: "Casual" },
      { value: "Gifting Someone" },
    ],
  },
  {
    key: "budget",
    title: "What is your budget?",
    options: [
      { value: "Under ₹5,000" },
      { value: "₹5,000 – ₹25,000" },
      { value: "₹25,000 – ₹1 Lakh" },
      { value: "₹1 – ₹5 Lakh" },
      { value: "₹5 Lakh+" },
      { value: "No limit" },
    ],
  },
  {
    key: "metal",
    title: "Which metal do you prefer?",
    options: [
      { value: "Yellow Gold" },
      { value: "White Gold" },
      { value: "Rose Gold" },
      { value: "Silver" },
      { value: "Diamond" },
      { value: "No preference" },
    ],
  },
];

export default function StyleMatch() {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const current = STEPS[step];
  const progress = ((step + (results ? 1 : 0)) / STEPS.length) * 100;

  const choose = (value) => {
    const next = { ...profile, [current.key]: value };
    setProfile(next);
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      submit(next);
    }
  };

  const submit = async (p) => {
    setLoading(true);
    try {
      const r = await api.post("/style-match", p);
      setResults(r.data.recommendations);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate recommendations");
    } finally {
      setLoading(false);
    }
  };

  const askMore = (item) => {
    sessionStorage.setItem(
      "mineai_pending_msg",
      `Tell me more about ${item.name} — fair price, what to check, and how to spot a good one.`,
    );
    navigate("/chat");
  };

  const reset = () => {
    setStep(0);
    setProfile({});
    setResults(null);
  };

  return (
    <div data-testid="style-match-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
          Style Match
        </p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128] tracking-tight">
          Find your <span className="gold-text">perfect jewellery</span>.
        </h1>

        {/* Progress */}
        <div className="mt-10 mb-8" data-testid="step-progress">
          <div className="flex items-center justify-between text-xs text-[#4A5568] mb-2">
            <span>
              {results
                ? "Done"
                : `Step ${step + 1} of ${STEPS.length}`}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 bg-[#E8DFD1] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#3B5BDB] smooth"
              style={{ width: `${progress}%`, transition: "width 400ms ease" }}
            />
          </div>
        </div>

        {loading && (
          <div className="mc-card p-12 text-center" data-testid="loading">
            <CircleNotch size={32} className="text-[#3B5BDB] animate-spin mx-auto" weight="bold" />
            <p className="mt-4 text-[#4A5568]">Mine AI is curating 6 pieces for you...</p>
          </div>
        )}

        {!loading && !results && current && (
          <div data-testid={`step-${current.key}`}>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0A1128]">
              {current.title}
            </h2>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {current.options.map((o) => (
                <button
                  key={o.value}
                  data-testid={`option-${current.key}-${o.value}`}
                  onClick={() => choose(o.value)}
                  className="mc-card lift smooth p-5 text-left hover:border-[#3B5BDB]"
                >
                  {o.color && (
                    <span
                      className="inline-block h-8 w-8 rounded-full border border-[#E8DFD1] mb-3"
                      style={{ background: o.color }}
                    />
                  )}
                  <p className="font-display text-lg text-[#0A1128]">{o.value}</p>
                  {o.desc && (
                    <p className="text-xs text-[#4A5568] mt-1">{o.desc}</p>
                  )}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                data-testid="back-btn"
                onClick={() => setStep(step - 1)}
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#3B5BDB] smooth"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
          </div>
        )}

        {!loading && results && (
          <div data-testid="results">
            <div className="mc-card p-6 mb-6 bg-[#3B5BDB]/5 border-[#3B5BDB]/20">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="duotone" className="text-[#D4AF37]" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
                  Your profile
                </p>
              </div>
              <p className="mt-2 text-sm text-[#0A1128]">
                {Object.values(profile).join(" · ")}
              </p>
              <button
                onClick={reset}
                data-testid="restart-btn"
                className="mt-3 text-xs text-[#3B5BDB] underline"
              >
                Restart quiz
              </button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {results.map((item, i) => (
                <div
                  key={i}
                  className="mc-card lift smooth p-6 fade-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                  data-testid={`recommendation-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-xl text-[#0A1128]">
                      {item.name}
                    </h3>
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded shrink-0">
                      {item.city}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">
                    {item.why_it_suits}
                  </p>
                  <div className="mt-4 pt-4 border-t border-[#E8DFD1] space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#4A5568]">Price range</span>
                      <span className="text-[#0A1128] font-medium tabular-nums">
                        ₹{Number(item.price_min).toLocaleString("en-IN")} – ₹
                        {Number(item.price_max).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4A5568]">Making charge</span>
                      <span className="text-[#0A1128]">{item.making_charge}</span>
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-[#4A5568] italic flex gap-2">
                    <Check size={14} className="text-[#16A34A] shrink-0 mt-0.5" weight="bold" />
                    {item.tip}
                  </p>
                  <button
                    data-testid={`ask-more-${i}`}
                    onClick={() => askMore(item)}
                    className="mt-4 text-sm text-[#3B5BDB] font-medium hover:gap-2 inline-flex items-center gap-1 smooth"
                  >
                    Ask Mine AI more <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
